import * as angular from 'angular';
import * as _ from 'lodash';

import { Store, Dispatcher, GetStore } from '../Store';
import { Identifier, Group } from '../../models';
import { duplicateIdentifier, duplicateIdentifierSuccess, duplicateIdentifierError, deleteIdentifier } from './identifiers.actions';
import { IGroupService } from '../../services/';
import { IDENTIFIER_DELETE_SUCCESS, IDENTIFIER_DELETE_ERROR, IDENTIFIERS_SAVE_SUCCESS, IDENTIFIERS_SAVE, IDENTIFIER_ADD_SUCCESS, IDENTIFIERS_SAVE_ERROR, IDENTIFIERS_REORDER, IDENTIFIERS_REORDER_ERROR, IDENTIFIERS_REORDER_SUCCESS } from './identifiers.constants';
import { IValueViewModelActionsService } from '../valueViewModels/valueViewModels.actionCreator';
import { IGroupActionsService } from '../groups/groups.actionCreator';
import { ITagDefinitionActionsService } from '../tags/tags.actionCreator';

export interface IIdentifiersActionsService {
    deleteOne(groupGuid: string, identifierGuid: string): Dispatcher<void>;
    // identifiers are loaded trough IGroupActionService
    duplicate(identifier: Identifier, targetGroup: Group, newsIdentifierName: string, newTagDefinitionGuid: string): Dispatcher<Identifier>;
    add(identifier: Identifier, group: Group): Dispatcher<Identifier>;
    updateOne(identifier: Identifier, group: Group): Dispatcher<Identifier>;
    updateMany(identifiers: Identifier[], group: Group): Dispatcher<Identifier[]>;
    redorderIdentifiers(identifiers: Identifier[], group: Group): Dispatcher<Identifier[]>;
    forceInMemoryUpdate(updated: Identifier[], isPartial?: boolean): Dispatcher<void>;
}

export class IdentifiersActionsService implements IIdentifiersActionsService {
    static serviceId = 'identifiersActionsService';

    static $inject = [
        '$q',
        'groupService',
        'valueViewModelActionsService',
        'groupActionsService',
        'tagDefinitionActionsService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _groupService: IGroupService,
        private _valueViewModelActionsService: IValueViewModelActionsService,
        private _groupActionsService: IGroupActionsService,
        private _tagDefinitionActionService: ITagDefinitionActionsService
    ) {
    }

    deleteOne(groupGuid: string, identifierGuid: string) {
        return (dispatch: any, getState: () => Store) => {
            dispatch(deleteIdentifier());
            return this._groupService
                .deleteIdentifier(groupGuid, identifierGuid)
                .then(data => {
                    const group = getState().groups.dictionary[groupGuid];
                    this.refreshDependency(groupGuid, dispatch, getState);
                    return this._$q.all([
                        dispatch({ type: IDENTIFIER_DELETE_SUCCESS, payload: { guid: identifierGuid, group } })
                    ]);
                })
                .catch(() => dispatch({ type: IDENTIFIER_DELETE_ERROR, payload: {} }));
        };
    }

    duplicate(identifier: Identifier, targetGroup: Group, newsIdentifierName: string, newTagDefinitionGuid: string): (dispatch: any, getState: () => Store) => angular.IPromise<Identifier> {
        return (dispatch: any, getState: () => Store) => {
            dispatch(duplicateIdentifier());
            return this._groupService
                .duplicateIdentifier(targetGroup.guid, identifier.guid, newsIdentifierName, newTagDefinitionGuid)
                .then(data => {
                    const result = data;
                    this.refreshDependency(targetGroup.guid, dispatch, getState);
                    return this._$q
                        .all([
                            dispatch(duplicateIdentifierSuccess({ identifier: result, group: targetGroup }))
                        ])
                        .then(() => result);
                })
                .catch(reason => {
                    dispatch(duplicateIdentifierError(reason));
                    return reason;
                });
        };
    }

    add(identifier: Identifier, group: Group): Dispatcher<Identifier> {
        return (dispatch: any, getState: GetStore) => {
            dispatch({ type: IDENTIFIERS_SAVE, payload: {} });
            return this._groupService
                .addIdentifier(group.guid, identifier)
                .then(added => {
                    this.refreshDependency(group.guid, dispatch, getState);
                    return this._$q
                        .all([
                            dispatch({ type: IDENTIFIER_ADD_SUCCESS, payload: { identifier: added, group } })
                        ])
                        .then(() => added);
                });
        };
    }

    updateOne(identifier: Identifier, group: Group): Dispatcher<Identifier> {
        return (dispatch: any, getState: GetStore) => {
            dispatch({ type: IDENTIFIERS_SAVE, payload: {} });
            return this._groupService
                .updateIdentifiers(group.guid, [identifier])
                .then(result => {
                    const identifiers = result.filter(d => d.guid === identifier.guid);
                    this.refreshDependency(group.guid, dispatch, getState);
                    return this._$q
                        .all([
                            dispatch({ type: IDENTIFIERS_SAVE_SUCCESS, payload: { identifiers, group } })
                        ])
                        .then(() => identifiers[0]);
                })
                .catch(() => dispatch({ type: IDENTIFIERS_SAVE_ERROR, payload: {} }));
        };
    }

    updateMany(identifiers: Identifier[], group: Group): Dispatcher<Identifier[]> {
        return (dispatch: any, getState: GetStore) => {
            dispatch({ type: IDENTIFIERS_SAVE, payload: {} });
            return this._groupService
                .updateIdentifiers(group.guid, identifiers)
                .then(identifiers => {
                    this.refreshDependency(group.guid, dispatch, getState);
                    return this._$q
                        .all([
                            dispatch({ type: IDENTIFIERS_SAVE_SUCCESS, payload: { identifiers, group } })
                        ])
                        .then(() => identifiers);
                })
                .catch(() => dispatch({ type: IDENTIFIERS_SAVE_ERROR, payload: {} }));
        };
    }

    redorderIdentifiers(identifiers: Identifier[], group: Group): Dispatcher<Identifier[]> {
        return (dispatch: any, getState: GetStore) => {
            dispatch({ type: IDENTIFIERS_REORDER, payload: {} });
            const sorted = _.sortBy(identifiers, i => i.sortingOrder).map(i => i.guid);
            return this._groupService
                .reorderIdentifiers(group.guid, sorted)
                .then(identifiers => {
                    dispatch({ type: IDENTIFIERS_REORDER_SUCCESS, payload: { identifiers, group } });
                    return identifiers;
                })
                .catch(e => {
                    console.log(e);
                    return dispatch({ type: IDENTIFIERS_REORDER_ERROR, payload: {} });
                });
        };
    }

    forceInMemoryUpdate(updated: Identifier[], isPartial: boolean = false): Dispatcher<void> {
        return (dispatch: any, getState: GetStore) => {
            dispatch({ type: IDENTIFIERS_SAVE_SUCCESS, payload: { identifiers: updated, isPartial: isPartial } });
            return this._$q.resolve();
        };
    }

    private refreshDependency(groupGuid, dispatch: any, getState: GetStore) {
        this._valueViewModelActionsService.getAll(true)(dispatch, getState);
        this._tagDefinitionActionService.getAll(true)(dispatch, getState);
        this._groupActionsService.getOne(groupGuid, true)(dispatch, getState);
    }
}